import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { SkillResourceSteps } from '../../models/Timeline';
import { Calculator, Eye, PencilRuler, Youtube } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { getTimelineButtonLabel, getTimelineButtonType } from '../../utils/timelineHandler';
import { useUserActionMutation } from '../../custom-hooks/useUserActionMutation';
import { SkillResourceCreateTimelineEventMutation } from '../../graphQLOperations/__generated__/SkillResourceCreateTimelineEventMutation.graphql';
import { timeLineMutation } from '../../graphQLOperations/SkillResource';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { BrainBreakPlacement } from '../../models/BrainBreak';
import { Menubar } from 'primereact/menubar';
import AdditionalResources from './AdditionalResources';
import { useParams } from 'react-router-dom';
import { useCooldown } from '../../contexts/RetakeCountdownContext';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { requestFullscreen } from '../../utils/fullscreen';

interface Props {
  currentStep: SkillResourceSteps | null;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  setSkipNewActivityId: Dispatch<SetStateAction<boolean>>;
  refetchLrnSession: () => void;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  setDisabledReviewModal: Dispatch<SetStateAction<boolean>>;
  setLearnosityPPKey: Dispatch<SetStateAction<number | null>>;
  skillId: string;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
}

const timelinePlacementMap: Partial<Record<SkillResourceSteps, BrainBreakPlacement>> = {
  [SkillResourceSteps.ABOUT_THE_SKILL]: 'about-the-skill',
  [SkillResourceSteps.WORKED_EXAMPLE]: 'worked-example',
  [SkillResourceSteps.PRACTICE_PROBLEMS]: 'practice-problems',
};

const TimeLineWrapper: FC<Props> = ({
  currentStep,
  setCurrentStep,
  setSkipNewActivityId,
  refetchLrnSession,
  setExpanded,
  setDisabledReviewModal,
  setLearnosityPPKey,
  skillId,
  setAssessmentInProgress,
}) => {
  // Guided-tour preview: the timeline user-action mutation below is a backend
  // write — guarded locally.
  const previewMode = usePreviewMode();
  const { skillCode } = useParams();
  const user = useAppSelector(selectUser);
  const handleBrainBreakNavigation = useBrainBreakNavigation();
  const [commitTimelineEvent] = useUserActionMutation<SkillResourceCreateTimelineEventMutation>(timeLineMutation);
  const [isFixed, setIsFixed] = useState(false);

  const cooldownCtx = useCooldown();
  const isBlocked = cooldownCtx?.isBlocked(skillCode || '');

  const onClickHandler = (e: React.MouseEvent | React.KeyboardEvent, option: SkillResourceSteps, item: MenuItem) => {
    const placement = timelinePlacementMap[currentStep as SkillResourceSteps];

    if (option !== SkillResourceSteps.SKILL_CHALLENGE) {
      setDisabledReviewModal(false);
      if (!previewMode) {
        commitTimelineEvent({
          variables: {
            input: {
              userAction: {
                timeStamp: new Date().toISOString(),
                description: item.label ?? '',
                actionTypeId: `ActionTypeNode:${item.data.action}`,
                userId: `UserNode:${user?.id}`,
                skillId: skillId,
              },
            },
          },
        });
      }
    } else {
      if (!isBlocked) {
        // AssessmentWrapper only mounts (and owns useExitFullscreenOnUnmount) when the retake
        // isn't blocked — requesting fullscreen here otherwise would strand the student on the
        // cooldown countdown with no unmount to exit them. Also skip during the guided tour,
        // which walks this same screen without triggering a real assessment.
        if (!previewMode) {
          requestFullscreen();
        }
        setAssessmentInProgress(true);
        setDisabledReviewModal(true);
      }
      setSkipNewActivityId(false);
      //refetch to start a new skill challenge
      refetchLrnSession();
    }

    if (option === SkillResourceSteps.PRACTICE_PROBLEMS) {
      setLearnosityPPKey(new Date().getTime());
    }

    if (placement) {
      handleBrainBreakNavigation(
        e as React.MouseEvent,
        () => {
          window.scrollTo({ top: 0, behavior: 'instant' });
          if (option === SkillResourceSteps.SKILL_CHALLENGE) {
            if (!isBlocked) {
              setAssessmentInProgress(true);
            }
            refetchLrnSession();
          }
          setCurrentStep(option);
        },
        placement,
      );
      return;
    } else {
      if (option === SkillResourceSteps.SKILL_CHALLENGE) {
        if (!isBlocked) {
          setAssessmentInProgress(true);
        }
        refetchLrnSession();
      }
      setCurrentStep(option);
    }
  };

  const itemRenderer = (item: MenuItem, option: SkillResourceSteps) => (
    <button
      type="button"
      role="button"
      className={`nc_timeline-menu-item tw-justify-center lg:tw-justify-start ${
        currentStep === option ? 'selected' : ''
      }`}
      onClick={e => onClickHandler(e, option, item)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickHandler(e, option, item);
        }
      }}
    >
      <span className="tw-text-[#686868] tw-h-[24px]" aria-hidden="true">
        {item.icon}
      </span>
      <div
        className={`tw-flex tw-flex-col tw-gap-1 ${
          (currentStep as number) + 1 === option ? 'tw-hidden md:tw-block' : 'tw-hidden'
        } lg:tw-block`}
      >
        <h1 className="tw-font-bold tw-text-sm tw-font-atkinson tw-text-[#686868]">{item.label}</h1>
        {item.data.button && (
          <Button
            className="tw-font-atkinson tw-text-sm"
            label={getTimelineButtonLabel(option)}
            outlined={getTimelineButtonType(option)}
            rounded={getTimelineButtonType(option)}
          />
        )}
        {item.data.timeStamp && <span className="tw-font-atkinson">{item.data.timestamp}</span>}
      </div>
    </button>
  );

  const items: MenuItem[] = [
    {
      label: 'About the skill',
      icon: <Eye />,
      template: item => itemRenderer(item, SkillResourceSteps.ABOUT_THE_SKILL),
      data: { button: false, action: 4 },
      command: () => {},
    },
    {
      label: 'Worked Example Video',
      icon: <Youtube />,
      template: item => itemRenderer(item, SkillResourceSteps.WORKED_EXAMPLE),
      data: { button: currentStep === SkillResourceSteps.ABOUT_THE_SKILL ? true : false, action: 4 },
    },

    {
      label: 'Practice Problems',
      icon: <Calculator />,
      template: item => itemRenderer(item, SkillResourceSteps.PRACTICE_PROBLEMS),
      data: { button: currentStep === SkillResourceSteps.WORKED_EXAMPLE ? true : false, action: 4 },
    },

    {
      label: 'Skill Challenge',
      icon: <PencilRuler />,
      template: item => itemRenderer(item, SkillResourceSteps.SKILL_CHALLENGE),
      data: { button: currentStep === SkillResourceSteps.PRACTICE_PROBLEMS ? true : false, action: 5 },
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setExpanded(false);
      setIsFixed(window.scrollY >= 275);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="tw-flex tw-flex-row lg:tw-flex-col tw-gap-4 tw-items-center">
      <Menu
        model={items}
        className={`nc_block nc_timeline-menu-vertical tw-hidden lg:tw-block ${isFixed ? 'fixed-active' : ''} `}
      />
      <Menubar
        model={items}
        className={`nc_block nc_timeline-menu-horizontal tw-flex lg:tw-hidden ${isFixed ? 'fixed-active' : ''} `}
      />
      <AdditionalResources isFixed={isFixed} skillId={skillId} />
    </div>
  );
};

export default TimeLineWrapper;
